import React, { useEffect } from "react";

/**
 * Extract YouTube video ID from various URL formats.
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/
 * Returns video ID string or null.
 */
export const extractYouTubeId = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    // youtube.com/watch?v=VIDEO_ID
    if (urlObj.hostname.includes("youtube.com") && urlObj.searchParams.get("v")) {
      return urlObj.searchParams.get("v");
    }
    // youtu.be/VIDEO_ID
    if (urlObj.hostname === "youtu.be") {
      return urlObj.pathname.slice(1).split("/")[0];
    }
    // youtube.com/embed/VIDEO_ID
    if (urlObj.hostname.includes("youtube.com") && urlObj.pathname.startsWith("/embed/")) {
      return urlObj.pathname.split("/embed/")[1].split("?")[0];
    }
    // youtube.com/shorts/VIDEO_ID
    if (urlObj.hostname.includes("youtube.com") && urlObj.pathname.startsWith("/shorts/")) {
      return urlObj.pathname.split("/shorts/")[1].split("?")[0];
    }
  } catch {
    return null;
  }
  return null;
};

const YouTubeLightbox = ({ videoUrl, isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    // Prevent body scroll while lightbox is open
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const videoId = extractYouTubeId(videoUrl);

  if (!videoId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[100]">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition z-[101]"
        aria-label="Close video"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Video Container - 16:9 aspect ratio */}
      <div className="w-full max-w-4xl mx-4">
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="Video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export default YouTubeLightbox;
